/**
 * clsx takes multiple arguments (strings, arrays, or falsy values), recursively
 * flattens arrays, filters out falsy values, and joins the remaining strings
 * with spaces to produce a single className string.
 *
 * We replaced an external dep with this to avoid use of `any` in type
 * definitions, which had allowed invalid usage like `clsx({width: '100px'})`
 * to slip through. Our implementation provides stricter type safety and only
 * allows patterns we actually use: strings, undefined, false, null, and arrays.
 *
 * @example
 * clsx('foo', 'bar') // 'foo bar'
 * clsx('foo', undefined, 'bar') // 'foo bar'
 * clsx('foo', false && 'bar', 'baz') // 'foo baz'
 * clsx(['foo', 'bar'], 'baz') // 'foo bar baz'
 */

export type ClassName = string | false | undefined | null
export type ClassNameArg = ClassName | ClassNameArg[]

export function clsx(...args: ClassNameArg[]) {
  const classes: string[] = []

  for (const arg of args) {
    if (!arg) continue

    if (Array.isArray(arg)) {
      const nested = clsx(...arg)
      if (nested) classes.push(nested)
    } else {
      classes.push(arg)
    }
  }

  return classes.join(' ')
}
